import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PrintReportButton from './PrintReportButton';
import ReactSelect from 'react-select';
import { getSessionById, deleteSession, closeSession, postSession } from '../../service/SessionService';
import Session from '../../models/Session';
import SessionItem from '../../models/SessionItem';
import { StatusBadge, STATUS_OPTIONS, calculateStatus } from './StatusBadge';
import { convertStrDateUTCToStrDateGB, formatInputType } from '../../utils/Utils';
import ConfirmationModal from "../../components/Shared/ConfirmationModal";
import PrintWindow from './PrintWindow';
import ItemEditModal from './ItemEditModal';
import { useAuthContext } from '../../contexts/AuthContext';
import { Brand } from '../../models/Brand';

const ScanningSessionDetail = () => {
    const { user } = useAuthContext();
    const isBmw = user?.brand === Brand.BMW;
    const { id } = useParams();
    const navigate = useNavigate();
    const [session, setSession] = useState<Session>({} as Session);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showModalCloseSession, setShowModalCloseSession] = useState(false);
    const [showModalDeleteSession, setShowModalDeleteSession] = useState(false);
    const [showModalPostSession, setShowModalPostSession] = useState(false);
    const [closingSession, setClosingSession] = useState(false);
    const [deletingSession, setDeletingSession] = useState(false);
    const [postingSession, setPostingSession] = useState(false);
    const [refreshingSession, setRefreshingSession] = useState(false);
    const [errorModal, setErrorModal] = useState({ show: false, message: '' });
    const [editingItem, setEditingItem] = useState<{ item: SessionItem; initialTab: 'edit' | 'history' } | null>(null);
    
    // Define the type of possible status values
    type StatusValue = (typeof STATUS_OPTIONS)[number]['value'];

    // Define the shape of the search state
    type SearchState = {
        partNumber: string;
        description: string;
        deliveryNotes: string;
        status: StatusValue[];
    };

    const [search, setSearch] = useState<SearchState>({
        partNumber: '',
        description: '',
        deliveryNotes: '',
        status: [],
    });

    const goBack = () => {
        navigate('/dashboard/scanning-session');
    };

    const handleDeleteSession = async () => {
        try {
            setDeletingSession(true);
            await deleteSession(session.id);
            setShowModalDeleteSession(false);
            goBack();
        } catch (error) {
            console.error(`Failed to delete session:`, error);
            const errorMessage = `Failed to delete session. Please try again.`;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setDeletingSession(false);
        }
    }

    const handleCloseSession = async () => {
        try {
            setClosingSession(true);
            await closeSession(session.id);
            goBack();
        } catch (error) {
            console.error(`Failed to close session:`, error);
            const errorMessage = `Failed to close session. Please try again.`;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setClosingSession(false);
        }
    };

    const handleRefreshSession = async () => {
        try {
            setRefreshingSession(true);
            const refreshedSession = await getSessionById(id as string);
            setSession(refreshedSession);
        } catch (error) {
            console.error(`Failed to refresh session:`, error);
            const errorMessage = `Failed to refresh session. Please try again.`;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setRefreshingSession(false);
        }
    };

    const handlePostSession = async () => {
        try {
            setPostingSession(true);
            await postSession(session.id);
            setShowModalPostSession(false);
            const refreshedSession = await getSessionById(id as string);
            setSession(refreshedSession);
        } catch (error) {
            console.error(`Failed to post session:`, error);
            const errorMessage = `Failed to post session. Please try again.`;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setPostingSession(false);
        }
    };

    useEffect(() => {
        const fetchSessionDetail = async () => {
            setIsLoading(true);
            try {
                const session = await getSessionById(id as string);
                setSession(session);
            } catch (error) {
                console.error('Failed to fetch session details:', error);
                setError('Failed to load session details. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSessionDetail();
    }, [id]);

    if (isLoading) {
        return (
            <div className="dashboard-section">
                <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
                    <div className="animate-pulse">Loading session details...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-section">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                    <div className="text-center text-red-600">{error}</div>
                    <div className="mt-4 text-center">
                        <button
                            onClick={goBack}
                            className="gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!session) {
        return (
            <div className="dashboard-section">
                <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
                    <div>Session not found</div>
                    <div className="mt-4">
                        <button
                            onClick={goBack}
                            className="gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Helper to filter items
    const filteredItems = session.items?.filter(item =>
        (search.partNumber === '' || item.partNumber?.toLowerCase().includes(search.partNumber.toLowerCase())) &&
        (search.description === '' || item.description?.toLowerCase().includes(search.description.toLowerCase())) &&
        (search.deliveryNotes === '' || (item.deliveryNotes?.join(', ') || '').toLowerCase().includes(search.deliveryNotes.toLowerCase())) &&
        (search.status.length === 0  || search.status.includes(calculateStatus(item.quantity, item.shipQtd)))
    );

    return (
        <section className="dashboard-section">
            {/* Header Section */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
                <div className="bg-white border-b border-gray-200 py-2 pb-6 flex items-center justify-between">
                    <div className="flex">
                        <button
                            onClick={goBack}
                            className="mr-4 text-gray-600 hover:text-gray-900"
                        >
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <h2 className="text-xl font-semibold text-gray-800">{session.title}</h2>
                    </div>
                    <div>
                        {session.posted ? (
                            <span className="inline-block px-3 py-1 rounded-full font-semibold bg-slate-100 text-slate-600">
                                <i className="fas fa-archive mr-1"></i>Posted
                            </span>
                        ) : session.closed ? (
                            <span className="inline-block px-3 py-1 rounded-full font-semibold bg-green-100 text-green-700">
                                Closed
                            </span>
                        ) : (
                            <span className="inline-block px-3 py-1 rounded-full font-semibold bg-yellow-100 text-yellow-700">
                                Open
                            </span>
                        )}
                    </div>
                </div>

                {/* Delivery Notes + optional WIP description */}
                <div className="my-6 space-y-3">
                    <div>
                        <p className="text-sm text-gray-600">Delivery Notes</p>
                        <p className="font-medium">
                            {session.deliveryNotes && session.deliveryNotes.length > 0
                                ? session.deliveryNotes.join(', ')
                                : 'None'}
                        </p>
                    </div>
                    {isBmw && session.description && (
                        <div>
                            <p className="text-sm text-gray-600">WIP Numbers</p>
                            <p className="font-medium">{session.description}</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-left gap-10 my-6">
                    <div>
                        <p className="text-sm text-gray-600">Created Date</p>
                        <p className="font-medium">{session.createdDate}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Created By</p>
                        <p className="font-medium">{session.createdByName}</p>
                    </div>

                    { session.closed &&
                        <>
                            <div>
                                <p className="text-sm text-gray-600">Closed Date</p>
                                <p className="font-medium">{session.closedDate}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Closed By</p>
                                <p className="font-medium">{session.closedByName}</p>
                            </div>
                        </>
                    }
                    { session.posted &&
                        <>
                            <div>
                                <p className="text-sm text-gray-600">Posted Date</p>
                                <p className="font-medium">{session.postedDate ? convertStrDateUTCToStrDateGB(session.postedDate) : '—'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Posted By</p>
                                <p className="font-medium">{session.postedByName}</p>
                            </div>
                        </>
                    }
                    
                    <div>
                        <p className="text-sm text-gray-600">Lines total</p>
                        <p className="font-medium">{session.items?.length}</p>
                    </div>

                </div>
                <div className="flex items-center justify-left mt-6 gap-4">
                    { session.closed ? (
                        <>
                            <div>
                                <PrintReportButton sessionId={session.id} sessionTitle={session.title} />
                            </div>
                            <div>
                                <PrintWindow session={session} isBmw={isBmw} filteredItems={filteredItems ?? []} />
                            </div>
                            {!session.posted && (
                                <div>
                                    <button
                                        onClick={() => setShowModalPostSession(true)}
                                        className="bg-slate-500 hover:bg-slate-600 text-white text-xs font-semibold py-1.5 px-3 rounded-lg transition"
                                        title="Post Session — permanently finalizes this session and prevents further edits"
                                        disabled={postingSession}
                                    >
                                        <i className="fas fa-archive mr-1"></i>
                                        Post Session
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div>
                                <button
                                    onClick={() => setShowModalCloseSession(true)}
                                    className={`bg-green-500 hover:bg-green-600 text-white text-xs font-semibold py-1.5 px-3 rounded-lg transition`}
                                    title="Close Session"
                                    disabled={closingSession}
                                >
                                    <i className="fas fa-lock mr-1"></i>
                                    Close Session
                                </button>
                            </div>
                            <div>
                                <button
                                    onClick={() => {
                                        const hasScannedItems = session.items?.some(item => (item.quantity ?? 0) > 0);
                                        if (hasScannedItems) {
                                            setErrorModal({ show: true, message: 'This session contains items with scanned quantities. Please zero out all quantities in the app before deleting the session.' });
                                        } else {
                                            setShowModalDeleteSession(true);
                                        }
                                    }}
                                    className={`bg-red-500 hover:bg-red-600 text-white text-xs font-semibold py-1.5 px-3 rounded-lg transition`}
                                    title="Delete Session"
                                    disabled={deletingSession}
                                >
                                    <i className="fas fa-trash mr-1"></i>
                                    Delete Session
                                </button>
                            </div>
                            <div>
                                <button
                                    onClick={handleRefreshSession}
                                    className={`bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold py-1.5 px-3 rounded-lg transition`}
                                    title="Refresh Session"
                                    disabled={refreshingSession}
                                >
                                    <i className="fas fa-refresh mr-1"></i>
                                    {refreshingSession ? 'Refreshing...' : 'Refresh Session'}
                                </button>
                            </div>
                            <div>
                                <PrintWindow session={session} isBmw={isBmw} filteredItems={filteredItems ?? []} />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Items Section - Placeholder for future implementation */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border" >
                        <thead>
                            <tr>
                                <th className="px-3 py-2 border-b align-top"></th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div className='text-left'>Part Number</div>
                                    <input
                                        id='idPartNumber'
                                        type="text"
                                        className="mt-1 w-full px-2 py-1 border rounded text-xs size-10"
                                        placeholder="Search..."
                                        value={search.partNumber}
                                        onChange={e => setSearch({ ...search, partNumber: e.target.value })}
                                    />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div className='text-left'>Description</div>
                                    <input
                                        id='idDescription'
                                        type="text"
                                        className="mt-1 w-full px-2 py-1 border rounded text-xs size-10"
                                        placeholder="Search..."
                                        value={search.description}
                                        onChange={e => setSearch({ ...search, description: e.target.value })}
                                    />
                                </th>
                                {isBmw && (
                                    <th className="px-3 py-2 border-b align-top">
                                        <div className='text-left'>Wip No.</div>
                                        <div style={{ height: '30px' }} />
                                    </th>
                                )}
                                <th className="px-3 py-2 border-b align-top">
                                    <div className='text-left'>Ship Qtd</div>
                                    <div style={{ height: '30px' }} />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div className='text-left'>Quantity</div>
                                    <div style={{ height: '30px' }} />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div className='text-left'>Input Type</div>
                                    <div style={{ height: '30px' }} />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div className='text-left'>Delivery Notes</div>
                                    <input
                                        id='idDeliveryNotes'
                                        type="text"
                                        className="mt-1 w-full px-2 py-1 border rounded text-xs size-10"
                                        placeholder="Search..."
                                        value={search.deliveryNotes}
                                        onChange={e => setSearch({ ...search, deliveryNotes: e.target.value })}
                                    />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div className='text-left'>Status</div>
                                    <ReactSelect
                                        isMulti
                                        options={STATUS_OPTIONS}
                                        value={STATUS_OPTIONS.filter(opt => search.status.includes(opt.value))}
                                        onChange={selected => setSearch({ ...search, status: selected.map(opt => opt.value) })}
                                        placeholder="Select status..."
                                        className="mt-1 text-xs"
                                        styles={{
                                            control: base => ({ ...base, minHeight: '38px', height: '38px', fontSize: '0.75rem' }),
                                            multiValue: base => ({ ...base, backgroundColor: '#e5e7eb', color: '#374151' }),
                                            multiValueLabel: base => ({ ...base, color: '#374151' }),
                                            multiValueRemove: base => ({ ...base, color: '#374151' }),
                                        }}
                                    />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div className='text-left'>Last Update</div>
                                    <div style={{ height: '30px' }} />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div className='text-left'>Actions</div>
                                    <div style={{ height: '30px' }} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems && filteredItems.length > 0 ? (
                                filteredItems.map((item, idx) => (
                                    <tr key={idx} className="border-b hover:bg-gray-50">
                                        <td className="px-3 py-2">{idx + 1}</td>
                                        <td className="px-3 py-2">
                                            <div>{item.partNumber}</div>
                                            {item.editedAt && (
                                                <span
                                                    className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 cursor-help"
                                                    title={`Edited by ${item.editedByName} on ${convertStrDateUTCToStrDateGB(item.editedAt)}`}
                                                >
                                                    Edited
                                                </span>
                                            )}
                                            {item.reconcileRefs && item.reconcileRefs.length > 0 && (
                                                <span
                                                    className="inline-block mt-0.5 ml-1 px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-700 cursor-help"
                                                    title={`Involved in ${item.reconcileRefs.length} group edit(s)`}
                                                >
                                                    <i className="fas fa-exchange-alt mr-1"></i>
                                                    ×{item.reconcileRefs.length}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">{item.description}</td>
                                        {isBmw && (
                                            <td className="px-3 py-2 text-center">{item.wipNo ?? '—'}</td>
                                        )}
                                        <td className="px-3 py-2">{item.shipQtd}</td>
                                        <td className="px-3 py-2">{item.quantity}</td>
                                        <td className="px-3 py-2">{formatInputType(item.inputType)}</td>
                                        <td className="px-3 py-2">{item.deliveryNotes?.join(', ')}</td>
                                        <td className="px-3 py-2">
                                            <StatusBadge quantity={item.quantity} shipQtd={item.shipQtd} />
                                        </td>
                                        <td className="px-3 py-2">{item.lastUpdate ? convertStrDateUTCToStrDateGB(item.lastUpdate) : ''}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {session.closed && !session.posted && (
                                                <button
                                                    onClick={() => setEditingItem({ item, initialTab: 'edit' })}
                                                    className="text-blue-500 hover:text-blue-700 transition mr-3"
                                                    title="Edit item"
                                                >
                                                    <i className="fas fa-pencil-alt"></i>
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setEditingItem({ item, initialTab: 'history' })}
                                                className="text-gray-500 hover:text-gray-700 transition"
                                                title="Show edit history"
                                            >
                                                <i className="fas fa-history"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={isBmw ? 11 : 10} className="text-center py-4 text-gray-400">
                                        No items found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmationModal
                show={showModalCloseSession}
                title={"Close Session"}
                message={"Are you sure you want to close this session? This action will mark the session as completed. This action cannot be undone."}
                confirmLabel={"Close"}
                confirmColor={"green"}
                onCancel={() => setShowModalCloseSession(false)}
                onConfirm={handleCloseSession}
                loading={closingSession}
            />

            <ConfirmationModal
                show={showModalDeleteSession}
                title={"Delete Session"}
                message={"Are you sure you want to delete this session? This action cannot be undone."}
                confirmLabel={"Delete"}
                confirmColor={"red"}
                onCancel={() => setShowModalDeleteSession(false)}
                onConfirm={() => handleDeleteSession()}
                loading={deletingSession}
            />

            <ConfirmationModal
                show={showModalPostSession}
                title={"Post Session"}
                message={"Are you sure you want to post this session? This will permanently finalize it — no further edits or reconciliation will be allowed. This action cannot be undone."}
                confirmLabel={"Post Session"}
                confirmColor={"slate"}
                onCancel={() => setShowModalPostSession(false)}
                onConfirm={handlePostSession}
                loading={postingSession}
            />

            {/* Item Edit / History Modal */}
            {editingItem && session.id && (
                <ItemEditModal
                    sessionId={session.id}
                    item={editingItem.item}
                    initialTab={editingItem.initialTab}
                    canEdit={!!session.closed && !session.posted}
                    onClose={() => setEditingItem(null)}
                    onSaved={(updatedItem) => {
                        setSession(prev => ({
                            ...prev,
                            items: prev.items?.map(i => i.id === updatedItem.id ? updatedItem : i),
                        }));
                        setEditingItem(null);
                    }}
                />
            )}

            {/* Error Modal */}
            {errorModal.show && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md text-center">
                        <h3 className="text-lg font-semibold text-red-600 mb-4">Error</h3>
                        <p className="text-gray-700 mb-4">{errorModal.message}</p>
                        <button
                            className="gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
                            onClick={() => setErrorModal({ show: false, message: '' })}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ScanningSessionDetail;